import { Fitout, STARTING_FITOUT } from "../../../shared/Fitout";
import { Boat } from "../boats/Boat";
import { Game } from "../Game";
import { SeaLife } from "../sea/SeaLife";
import { WakeField } from "../sea/WakeField";
import { Water } from "../sea/Water";
import { SprayField } from "../sea/SprayField";
import { Waves } from "../sea/Waves";
import { Scene } from "./Scene";

/** How long the preview boat holds a course before picking a new one, in seconds. */
const WANDER_HOLD_MIN = 2;
const WANDER_HOLD_MAX = 5;
/** Largest course change chosen at once, in radians. Keeps it sailing rather than spinning. */
const WANDER_TURN_MAX = Math.PI * 0.75;

/**
 * The weather out here is a number rather than a weather system: there is no arena on the main
 * menu to publish one, and all the sea wants is something to swing round to.
 *
 * The **water itself is the real one**, which it did not used to be. A sea that lives on the
 * client (design/Water.md) is the same sea wherever it is drawn, so the
 * attract screen runs the module a battle runs rather than a stand-in for it.
 */
const PREVIEW_WIND = 2.4;

/**
 * The preview sea, shown before the player enters a battle. The boat sails itself on a
 * random course so the screen is never still; nothing here is the real arena, and none of
 * this wandering survives into it — a battle takes its heading from the player.
 */
export class MainScene extends Scene {
    /**
     * The boat on the water, which is **the one in the locker** rather than a stand-in. That is
     * what makes the attract screen a preview: equipping something in the shop lands on a whole
     * ship under way rather than on a card, which is most of why the shop is an overlay and not a
     * screen of its own. See design/Shop.md.
     */
    private _fitout: Fitout;

    private _water: Water | null = null;
    private _seaLife: SeaLife | null = null;
    private _wakeField: WakeField | null = null;
    private readonly _sprayField = new SprayField();
    private _playerBoat: Boat | null = null;
    private _wanderTimer = 0;
    /** The preview sea, and where the camera has wandered to in it. See PREVIEW_WIND. */
    private readonly _waves = new Waves();
    private _driftX = 0;
    private _driftY = 0;


    constructor(fitout: Fitout = STARTING_FITOUT) {
        super();
        this._fitout = fitout;
    }

    /**
     * Where on the screen the preview boat is drawn, as fractions of the width and height.
     * Dead centre with nothing in the way.
     *
     * **The shop moves it, and that is the whole reason it exists.** design/Shop.md's claim is that
     * equipping a boat lands on a whole ship under way rather than on a card — which is only true
     * if the ship is not behind the panel. The shop is anchored to the top left and sized to its
     * own content, so the preview stands off into the clear water down and to the right of it while
     * it is open, and comes back to the middle when it shuts.
     *
     * It moves the **drawing**, not the sailing: the boat is still the camera, and the sea, the
     * wake and the spray all still come off its position, so nothing about how the preview moves
     * changes with it.
     */
    private _focusX = 0.5;
    private _focusY = 0.5;

    public focusOn(x: number, y: number): void {
        this._focusX = Math.min(1, Math.max(0, x));
        this._focusY = Math.min(1, Math.max(0, y));
        this.resize(Game.width, Game.height);
    }

    /** Refits the preview in place. The scene keeps sailing; only the ship on it changes. */
    public set fitout(value: Fitout) {
        this._fitout = value;
        if (this._playerBoat) this._playerBoat.fitout = value;
    }

    public enter(width: number, height: number): void {
        this._water = new Water(width, height, Game.app.renderer);
        this._container.addChild(this._water.container);

        // Inside the water, not beside it: the school is below the surface, so it goes under
        // the wave overlay and through the ocean filter with the sea. See design/SeaLife.md.
        this._seaLife = new SeaLife(width, height);
        this._water.underwater.addChild(this._seaLife.container);

        // Handed to the water rather than added to the scene — the sea is drawn through it. There
        // is no zoom out here, so the preview sea is where the wake dials are judged with nothing
        // else running. See design/Water.md.
        this._wakeField = new WakeField(width, height);
        this._water.setTrail(this._wakeField.field, this._wakeField.displacementStrength, this._wakeField.foamStrength);

        // A sibling of the water, not a child: anything inside the water container goes
        // through the ocean filter, which would ripple the hull along with the sea.
        this._playerBoat = new Boat(this._fitout);
        this._container.addChild(this._playerBoat.container);

        this._chooseNewCourse();
        this.resize(width, height);
    }

    public override update(deltaSeconds: number): void {
        this._wanderTimer -= deltaSeconds;
        if (this._wanderTimer <= 0) this._chooseNewCourse();

        this._playerBoat?.update(deltaSeconds);

        // The boat never leaves the centre, so its velocity is spent scrolling the sea. The
        // school is in that sea, so it takes the same offset and gets left astern.
        if (this._water && this._playerBoat) {
            const velocity = this._playerBoat.velocity;

            // The wave overlay still wants an absolute camera, so the preview keeps one of its
            // own: the boat never moves, so where it *would* have got to is the camera.
            this._driftX += velocity.x * deltaSeconds;
            this._driftY += velocity.y * deltaSeconds;
            // The waves want only how far it went this frame, because they are measured from the
            // camera outwards. See src/scripts/sea/Waves.ts.
            this._waves.update(deltaSeconds, PREVIEW_WIND, velocity.x * deltaSeconds, velocity.y * deltaSeconds);

            this._water.setCamera(this._driftX, this._driftY, 1);
            this._water.setWaves(this._waves.trains);
            // At the camera exactly, because the preview boat is always the middle of the screen.
            this._playerBoat.ride(this._waves.trains, 0, 0);
            this._seaLife?.scroll(velocity.x * deltaSeconds, velocity.y * deltaSeconds);
            this._wakeField?.scroll(velocity.x * deltaSeconds, velocity.y * deltaSeconds);
            this._sprayField.scroll(velocity.x * deltaSeconds, velocity.y * deltaSeconds);

            // After the scroll, or the gap to the last mark would be measured from a point the
            // sea has already carried away. The boat never leaves the centre, so that is where
            // its trail is laid from; the water does the moving. One id, because there is only
            // ever one hull out here.
            const { length, beam } = this._playerBoat.fitout.size;
            const position = this._playerBoat.container.position;
            this._wakeField?.trail("preview", position.x, position.y, this._playerBoat.heading, length, beam, false);
            this._sprayField.spray("preview", position.x, position.y, this._playerBoat.heading, length, this._waves.trains, deltaSeconds);
        }

        this._seaLife?.update(deltaSeconds);
        this._wakeField?.update(deltaSeconds);
        this._sprayField.update(deltaSeconds);
        this._water?.update(deltaSeconds);
    }

    public override resize(width: number, height: number): void {
        this._water?.resize(width, height);
        this._seaLife?.resize(width, height);
        this._wakeField?.resize(width, height);
        this._sprayField.resize(width, height);
        // The player never leaves their spot on the screen — the sea moves instead. Which spot is
        // `focusOn`, which the shop uses to stand the preview off out of its own way.
        this._playerBoat?.container.position.set(width * this._focusX, height * this._focusY);
    }

    /** Picks a fresh heading, relative to the current one, and how long to hold it. */
    private _chooseNewCourse(): void {
        this._wanderTimer = WANDER_HOLD_MIN + Math.random() * (WANDER_HOLD_MAX - WANDER_HOLD_MIN);

        if (this._playerBoat) {
            this._playerBoat.targetHeading = this._playerBoat.heading + (Math.random() * 2 - 1) * WANDER_TURN_MAX;
        }
    }
}
